// Store settings used by printable documents (header, logo, decimals, footer…).
//
// The legacy SPA reads them from `window.data` (injected by the MPS blade
// layout through mps_data()). The React admin is not always served by that
// layout, and public views have no session at all, so we fall back to fetching
// the MPS root page (`/admin/`, public) and parsing its `var data = {...};` line.

import { useEffect, useState } from 'react';

import { moduleRootUrl } from '../../api/client';

export type StoreSettings = {
  name?: string;
  short_name?: string;
  company?: string;
  email?: string;
  phone?: string;
  address?: string;
  default_logo?: string;
  default_customer?: string;
  header?: string;
  footer?: string;
  decimals?: string | number;
  quantity_decimals?: string | number;
  show_tax?: string | number;
  show_discount?: string | number;
  show_image?: string | number;
  show_tax_summary?: string | number;
  [key: string]: unknown;
};

export type PaymentBootSettings = {
  public_key?: string;
  gateway?: string;
  moduleURL?: string;
  paypal?: string | boolean;
};

export type BootData = {
  settings: StoreSettings;
  payment: PaymentBootSettings;
};

type LegacyWindowData = { settings?: { data?: StoreSettings; payment?: PaymentBootSettings } };

let bootPromise: Promise<BootData> | null = null;
let bootCache: BootData | null = null;

function fromLegacy(data: LegacyWindowData | undefined): BootData | null {
  const settings = data?.settings?.data;
  if (!settings || typeof settings !== 'object' || Object.keys(settings).length < 3) return null;
  return { settings, payment: data?.settings?.payment ?? {} };
}

export function parseBootData(html: string): BootData | null {
  const line = html.split('\n').find((entry) => entry.trim().startsWith('var data = '));
  if (!line) return null;
  const json = line.trim().replace(/^var data = /, '').replace(/;\s*$/, '');
  try {
    return fromLegacy(JSON.parse(json) as LegacyWindowData);
  } catch {
    return null;
  }
}

export function loadBootData(): Promise<BootData> {
  if (bootCache) return Promise.resolve(bootCache);
  const injected = fromLegacy((window as unknown as { data?: LegacyWindowData }).data);
  if (injected) {
    bootCache = injected;
    return Promise.resolve(injected);
  }
  bootPromise ??= fetch(`${moduleRootUrl()}/`, { credentials: 'include', headers: { Accept: 'text/html' } })
    .then((response) => response.text())
    .then((html) => parseBootData(html) ?? { settings: {}, payment: {} })
    .catch(() => ({ settings: {}, payment: {} }))
    .then((data) => {
      bootCache = data;
      return data;
    });
  return bootPromise;
}

export function useBootData(): BootData {
  const [data, setData] = useState<BootData>(() => bootCache ?? { settings: {}, payment: {} });
  useEffect(() => {
    let active = true;
    void loadBootData().then((next) => {
      if (active) setData(next);
    });
    return () => {
      active = false;
    };
  }, []);
  return data;
}

export function useStoreSettings(): StoreSettings {
  return useBootData().settings;
}
